import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatAud } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import StoreHeader from "../../components/layout/StoreHeader";
import StoreFooter from "../../components/layout/StoreFooter";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Shield, ShieldOff, Truck, MapPin, AlertTriangle, CheckCircle2, Clock, Store } from "lucide-react";

const STATES = ["VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

// ─── Collect-by date (today + 7 days) ────────────────────────────────
const collectByDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const { user }  = useCustomerAuth();

  const [settings, setSettings] = useState({
    cod_enabled: true, tax_rate: 0.1, shipping_flat: 15, free_shipping_threshold: 250,
    store_address: "Brisbane, QLD",
  });
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    line1: "", suburb: "", state: "QLD", postcode: "",
    payment_method: "stripe",
    notes: "",
  });

  // ── NEW states ──────────────────────────────────────────────────────
  const [fulfillmentType, setFulfillmentType] = useState("delivery"); // "delivery" | "click_collect"
  const [insurance,       setInsurance]       = useState(false);
  const [loading,         setLoading]         = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name || f.name, email: user.email || f.email }));
  }, [user]);

  const [shippingQuote, setShippingQuote] = useState(null); // {shipping, zone} from server
  const [quoteLoading,  setQuoteLoading]  = useState(false);

  // Live shipping quote — recalculates whenever postcode/state/cart/fulfillment changes.
  // Falls back to settings.shipping_flat if the quote call fails for any reason.
  useEffect(() => {
    if (fulfillmentType === "click_collect") {
      setShippingQuote({ shipping: 0, zone: null });
      return;
    }
    if (!form.postcode || form.postcode.length < 4 || items.length === 0) {
      setShippingQuote(null);
      return;
    }
    const t = setTimeout(() => {
      setQuoteLoading(true);
      api.post("/shipping/quote", {
        items: items.map(i => ({ sku: i.sku, qty: i.qty })),
        state: form.state,
        postcode: form.postcode,
        fulfillment_type: fulfillmentType,
      }).then(r => setShippingQuote(r.data))
        .catch(() => setShippingQuote(null))
        .finally(() => setQuoteLoading(false));
    }, 400); // debounce while typing postcode
    return () => clearTimeout(t);
  }, [form.postcode, form.state, fulfillmentType, items]);

  const totals = useMemo(() => {
    const taxRate = settings.tax_rate || 0.1;
    const gstIncluded = Math.round((subtotal - subtotal / (1 + taxRate)) * 100) / 100;
    let shipping;
    if (fulfillmentType === "click_collect") {
      shipping = 0;
    } else if (subtotal >= (settings.free_shipping_threshold || 0) && (settings.free_shipping_threshold || 0) > 0) {
      shipping = 0;
    } else if (shippingQuote) {
      shipping = shippingQuote.shipping;
    } else {
      shipping = settings.shipping_flat || 0; // fallback before postcode entered / quote fails
    }
    const base            = Math.round((subtotal + shipping) * 100) / 100;
    const insuranceAmount = insurance ? Math.max(Math.round(base * 0.10 * 100) / 100, 3) : 0;
    const total           = Math.round((base + insuranceAmount) * 100) / 100;
    return { gstIncluded, shipping, base, insuranceAmount, total };
  }, [subtotal, settings, fulfillmentType, insurance, shippingQuote]);

  if (items.length === 0) {
    return (
      <div className="bg-white min-h-screen">
        <StoreHeader />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 text-center">
          <div className="font-heading text-2xl font-black uppercase mb-3">Your cart is empty</div>
          <Link to="/shop" className="inline-flex h-11 px-6 bg-red-600 text-white font-heading uppercase font-bold items-center rounded-sm">
            Shop parts
          </Link>
        </div>
      </div>
    );
  }

  // Delivery requires address; C&C only needs contact details
  const canSubmit = fulfillmentType === "click_collect"
    ? !!(form.name && form.email)
    : !!(form.name && form.email && form.line1 && form.suburb && form.postcode);

  const onPlaceOrder = async () => {
    if (!canSubmit) {
      toast.error(fulfillmentType === "click_collect"
        ? "Please enter your name and email"
        : "Please complete all required fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, sku: i.sku, name: i.name,
          price: i.price, qty: i.qty, image_url: i.image_url,
        })),
        customer_name:    form.name,
        customer_email:   form.email,
        customer_phone:   form.phone,
        shipping_address: fulfillmentType === "click_collect"
          ? { line1: "Click & Collect — BCP Brisbane", suburb: "Brisbane", state: "QLD", postcode: "4000", country: "Australia" }
          : { line1: form.line1, suburb: form.suburb, state: form.state, postcode: form.postcode, country: "Australia" },
        payment_method:   form.payment_method,
        origin_url:       window.location.origin,
        notes:            form.notes,
        // ── NEW fields ──
        insurance:        insurance,
        insurance_amount: totals.insuranceAmount,
        fulfillment_type: fulfillmentType,
      };
      const { data } = await api.post("/orders", payload);
      if (data.checkout_url) {
        clear();
        window.location.href = data.checkout_url;
      } else {
        clear();
        navigate(`/order/confirmed?order_number=${encodeURIComponent(data.order.order_number)}`);
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "Failed to place order";
      if (status === 409) {
        toast.error(detail, {
          duration: 8000,
          description: "Please return to your cart and remove or reduce the quantity of the item.",
          action: { label: "Go to cart", onClick: () => window.history.back() },
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="checkout-page">
      <StoreHeader />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="text-red-600 font-mono text-xs uppercase tracking-[0.3em] mb-2">Checkout · 02</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-zinc-950 mb-8">
          Delivery & payment
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6">

            {/* ── FULFILMENT TYPE ─────────────────────────────────────── */}
            <section className="border border-zinc-200 rounded-sm p-6">
              <div className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-4">
                How would you like your order?
              </div>
              <div className="grid grid-cols-2 gap-3">
                {/* Delivery */}
                <button
                  type="button"
                  onClick={() => setFulfillmentType("delivery")}
                  data-testid="fulfillment-delivery"
                  className={`p-4 rounded-sm border-2 text-left transition-all ${
                    fulfillmentType === "delivery"
                      ? "border-red-600 bg-red-50"
                      : "border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="h-5 w-5" style={{ color: fulfillmentType === "delivery" ? "#e31e24" : "#94a3b8" }} />
                    <div className="font-heading font-bold uppercase text-sm">
                      Home Delivery
                    </div>
                    {fulfillmentType === "delivery" && (
                      <CheckCircle2 className="h-4 w-4 ml-auto" style={{ color: "#e31e24" }} />
                    )}
                  </div>
                  <div className="text-xs text-zinc-500">
                    Ship to your address · flat rate or free above threshold
                  </div>
                </button>

                {/* Click & Collect */}
                <button
                  type="button"
                  onClick={() => setFulfillmentType("click_collect")}
                  data-testid="fulfillment-click-collect"
                  className={`p-4 rounded-sm border-2 text-left transition-all ${
                    fulfillmentType === "click_collect"
                      ? "border-red-600 bg-red-50"
                      : "border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Store className="h-5 w-5" style={{ color: fulfillmentType === "click_collect" ? "#e31e24" : "#94a3b8" }} />
                    <div className="font-heading font-bold uppercase text-sm">
                      Click &amp; Collect
                    </div>
                    {fulfillmentType === "click_collect" && (
                      <CheckCircle2 className="h-4 w-4 ml-auto" style={{ color: "#e31e24" }} />
                    )}
                  </div>
                  <div className="text-xs text-zinc-500">
                    FREE · Pay now · collect from BCP Brisbane yard
                  </div>
                </button>
              </div>

              {/* ⚠️ Click & Collect 24hr warning */}
              {fulfillmentType === "click_collect" && (
                <div className="mt-3 rounded-sm border-2 border-amber-400 bg-amber-50 p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-[22px] shrink-0">⚠️</span>
                    <div>
                      <div className="font-heading font-bold uppercase text-[14px] text-amber-900 mb-1">
                        Please allow at least 24 hours
                      </div>
                      <div className="text-[13px] text-amber-800 leading-relaxed">
                        Click &amp; Collect orders need at least <strong>24 hours</strong> to be pulled, checked, and made ready for collection.
                        If you need the part <strong>urgently</strong>, please call us first and we may be able to assist straight away.
                      </div>
                      <a href={`tel:${settings.store_phone?.replace(/\s/g,"") || "0406291986"}`}
                        className="inline-flex items-center gap-1.5 mt-2 font-bold text-[13px] text-amber-900 hover:underline">
                        📞 Call {settings.store_phone || "0406 291 986"}
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Click & Collect details */}
              {fulfillmentType === "click_collect" && (
                <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50 p-4 space-y-2.5">
                  <div className="flex items-start gap-2.5">
                    <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                    <div>
                      <div className="font-heading font-bold text-sm text-zinc-900">BCP Brisbane — Pickup Yard</div>
                      <div className="text-xs text-zinc-600">{settings.store_address || "Brisbane, QLD"}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Clock className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                    <div>
                      <div className="font-bold text-xs text-zinc-900">Collect by: {collectByDate()}</div>
                      <div className="text-xs text-zinc-500">Part held for 7 days maximum</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-sm p-2.5">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-red-600" />
                    <div className="text-xs text-red-700 font-medium">
                      <strong>Important:</strong> If the part is not collected within 7 days, the order will be automatically cancelled.{" "}
                      <strong>No refunds are issued for uncollected orders.</strong> Please ensure you can collect before ordering.
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* ── CONTACT ──────────────────────────────────────────────── */}
            <section className="border border-zinc-200 rounded-sm p-6">
              <div className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-4">Contact</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide text-zinc-600">Full name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="checkout-name" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide text-zinc-600">Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" data-testid="checkout-email" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase tracking-wide text-zinc-600">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" data-testid="checkout-phone" />
                </div>
              </div>
            </section>

            {/* ── SHIPPING ADDRESS (delivery only) ────────────────────── */}
            {fulfillmentType === "delivery" && (
              <section className="border border-zinc-200 rounded-sm p-6">
                <div className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-4">Shipping address</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-xs font-mono uppercase tracking-wide text-zinc-600">Street address *</Label>
                    <Input value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} className="mt-1 rounded-sm" data-testid="checkout-line1" />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wide text-zinc-600">Suburb *</Label>
                    <Input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} className="mt-1 rounded-sm" data-testid="checkout-suburb" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-mono uppercase tracking-wide text-zinc-600">State *</Label>
                      <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                        <SelectTrigger className="mt-1 rounded-sm" data-testid="checkout-state"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase tracking-wide text-zinc-600">Postcode *</Label>
                      <Input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className="mt-1 rounded-sm" data-testid="checkout-postcode" />
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ── TRANSIT INSURANCE ────────────────────────────────────── */}
            <section className="border border-zinc-200 rounded-sm p-6" data-testid="insurance-section">
              <div className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-4">
                Transit Insurance (Optional)
              </div>

              {/* Liability notice */}
              <div className="flex items-start gap-3 p-3 rounded-sm bg-zinc-50 border border-zinc-200 mb-4">
                <ShieldOff className="h-5 w-5 shrink-0 mt-0.5 text-zinc-400" />
                <div className="text-xs text-zinc-600 leading-relaxed">
                  <strong className="text-zinc-900">Parts can be damaged in transit.</strong>{" "}
                  BCP Brisbane is not liable for damage that occurs during shipping.
                  Without insurance, credits for transit damage will be rejected.{" "}
                  <br />
                  <span className="text-zinc-500 mt-1 block">
                    Note: Credits for wrong or faulty parts supplied by BCP remain covered regardless of insurance,
                    subject to cross-verification before acceptance.
                  </span>
                </div>
              </div>

              {/* Insurance toggle */}
              <div
                onClick={() => setInsurance(!insurance)}
                className={`flex items-start gap-4 p-4 rounded-sm border-2 cursor-pointer transition-all ${
                  insurance ? "border-green-500 bg-green-50" : "border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid="insurance-toggle"
              >
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                  insurance ? "border-green-500 bg-green-500" : "border-zinc-300"
                }`}>
                  {insurance && <CheckCircle2 className="h-4 w-4 text-white" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="h-4 w-4 text-green-600" />
                    <div className="font-heading font-bold text-sm uppercase">
                      Add transit insurance
                    </div>
                    <span className="ml-auto text-sm font-mono font-bold text-green-700">
                      +{formatAud(Math.max(Math.round(totals.base * 0.10 * 100) / 100, 3))}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-600 leading-relaxed">
                    {insurance ? (
                      <span className="text-green-700 font-medium">
                        ✓ Your order is insured against transit damage. Credits for damaged goods will be accepted.
                      </span>
                    ) : (
                      <>
                        10% of order total (min $3.00). Covers damage that occurs during shipping.
                        Insured orders are eligible for full credit if items arrive damaged.
                      </>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* ── PAYMENT METHOD ────────────────────────────────────────── */}
            <section className="border border-zinc-200 rounded-sm p-6">
              <div className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-4">Payment method</div>
              <RadioGroup value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })} className="flex flex-col gap-3">
                <label className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer ${form.payment_method === "stripe" ? "border-red-600" : "border-zinc-200"}`}>
                  <RadioGroupItem value="stripe" id="pay-stripe" data-testid="payment-stripe" />
                  <div className="flex-1">
                    <div className="font-heading font-bold text-sm uppercase tracking-wide">Credit / Debit card</div>
                    <div className="text-xs text-zinc-500">Stripe secure checkout · Visa, Mastercard, Amex</div>
                  </div>
                </label>
                {settings.cod_enabled && fulfillmentType === "delivery" && (
                  <label className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer ${form.payment_method === "cod" ? "border-red-600" : "border-zinc-200"}`}>
                    <RadioGroupItem value="cod" id="pay-cod" data-testid="payment-cod" />
                    <div className="flex-1">
                      <div className="font-heading font-bold text-sm uppercase tracking-wide">Cash on Delivery</div>
                      <div className="text-xs text-zinc-500">Pay on delivery. Available for trade & local accounts.</div>
                    </div>
                  </label>
                )}
                {fulfillmentType === "click_collect" && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-700">
                    Click &amp; Collect orders must be paid online. Cash payment available in-person at the yard.
                  </div>
                )}
              </RadioGroup>
            </section>

            {/* Notes */}
            <section className="border border-zinc-200 rounded-sm p-6">
              <div className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-4">Order notes (optional)</div>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={fulfillmentType === "click_collect" ? "Preferred pickup time, contact name at yard, etc." : "Special instructions…"}
                className="w-full px-3 py-2 border border-zinc-200 rounded-sm text-sm resize-none outline-none focus:border-zinc-400"
              />
            </section>
          </div>

          {/* ── ORDER SUMMARY SIDEBAR ──────────────────────────────────── */}
          <aside className="border border-zinc-200 rounded-sm p-6 h-fit sticky top-28" data-testid="checkout-summary">
            <div className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-4">Order summary</div>

            {/* Fulfillment + insurance badges */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              <span className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 rounded-sm ${
                fulfillmentType === "click_collect"
                  ? "bg-blue-100 text-blue-700 border border-blue-200"
                  : "bg-zinc-100 text-zinc-600 border border-zinc-200"
              }`}>
                {fulfillmentType === "click_collect" ? <><Store className="h-2.5 w-2.5" /> Click &amp; Collect</> : <><Truck className="h-2.5 w-2.5" /> Delivery</>}
              </span>
              <span className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 rounded-sm ${
                insurance
                  ? "bg-green-100 text-green-700 border border-green-200"
                  : "bg-zinc-100 text-zinc-500 border border-zinc-200"
              }`}>
                {insurance ? <><Shield className="h-2.5 w-2.5" /> Insured</> : <><ShieldOff className="h-2.5 w-2.5" /> No Insurance</>}
              </span>
            </div>

            {/* C&C collect-by notice */}
            {fulfillmentType === "click_collect" && (
              <div className="mb-4 p-2.5 rounded-sm bg-amber-50 border border-amber-200 text-xs text-amber-700">
                <div className="font-bold mb-0.5">Collect by: {collectByDate()}</div>
                <div>Order cancelled after 7 days. No refunds.</div>
              </div>
            )}

            {/* Item list */}
            <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
              {items.map((i) => (
                <div key={i.product_id} className="flex justify-between gap-2 text-xs">
                  <span className="truncate">{i.qty}× {i.name}</span>
                  <span className="font-mono">{formatAud(i.price * i.qty)}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t border-zinc-200 pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-zinc-600">Subtotal</span><span>{formatAud(subtotal)}</span></div>
              <div className="flex justify-between text-zinc-400 text-xs">
                <span>GST included ({Math.round((settings.tax_rate || 0.1) * 100)}%)</span>
                <span>({formatAud(totals.gstIncluded)})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Shipping</span>
                <span>
                  {fulfillmentType !== "click_collect" && !shippingQuote && !form.postcode
                    ? <span className="text-zinc-400 text-xs">Enter postcode</span>
                    : quoteLoading
                    ? <span className="text-zinc-400 text-xs">Calculating…</span>
                    : totals.shipping === 0
                    ? <span className="text-green-600 font-semibold">FREE</span>
                    : formatAud(totals.shipping)}
                </span>
              </div>
              {insurance && (
                <div className="flex justify-between text-green-700">
                  <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Transit insurance</span>
                  <span>+{formatAud(totals.insuranceAmount)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline pt-2 mt-2 border-t border-zinc-200">
                <span className="font-heading uppercase">Total</span>
                <span className="font-heading font-black text-2xl" data-testid="checkout-total">{formatAud(totals.total)}</span>
              </div>
            </div>

            <Button
              onClick={onPlaceOrder}
              disabled={loading || !canSubmit}
              className="w-full mt-6 h-12 rounded-sm bg-red-600 hover:bg-red-700 font-heading font-bold uppercase tracking-wide"
              data-testid="checkout-place-order"
            >
              {loading
                ? "Processing…"
                : fulfillmentType === "click_collect"
                ? "Confirm & pay"
                : form.payment_method === "stripe" ? "Pay with card" : "Place order (COD)"}
            </Button>

            {/* Insurance reminder if not insured */}
            {!insurance && (
              <div className="mt-3 flex items-center gap-1.5 text-[10px] text-zinc-400 font-mono">
                <ShieldOff className="h-3 w-3" />
                No transit insurance — damage in shipping is not covered
              </div>
            )}

            <div className="mt-2 text-[11px] font-mono uppercase tracking-widest text-zinc-400 text-center">
              Stripe encrypted · PCI DSS
            </div>
          </aside>
        </div>
      </div>
      <StoreFooter />
    </div>
  );
}